import React, { useEffect, useMemo, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import type { Option } from "@components/FormItem/FormItemFieldRenderer";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { usePersistentForm } from "@hooks/usePersistentForm";
import type { DelayRequest, DelayView } from "@services/drybulkCargoOperationsApi";
import { convertUaeToUtc, getCurrentUaeDate } from "@utils/date-time";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DELAY_FORM_PERSIST_KEY } from "../constants";
import { useCachedCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useDelayFormStore } from "../hooks/useDelayFormStore";
import { useDelayMutations } from "../hooks/useDelayMutations";
import { useDelayCodes, useDelayTypes } from "../hooks/useDelayOptions";
import { useGatedOpen } from "../hooks/usePollingGate";
import { formatMinutes } from "../utils/formatMinutes";
import { problemMessage } from "../utils/problemDetail";
import { uaeWallClock } from "../utils/uaeWallClock";
import {
  delayDefaultValues,
  delaySchema,
  DELAY_FORM_FIELDS as FIELDS,
  L_DELAY,
  REMARKS_MAX_LENGTH,
  type DelayFormData,
  type DelayWindow,
} from "./delaySchemas";

const withSaved = (options: Option[], saved: Option | null): Option[] =>
  saved && !options.some((option) => option.value === saved.value) ? [...options, saved] : options;

const earliest = (dates: Date[]): Date | null =>
  dates.length === 0 ? null : new Date(Math.min(...dates.map((date) => date.getTime())));
const latest = (dates: Date[]): Date | null =>
  dates.length === 0 ? null : new Date(Math.max(...dates.map((date) => date.getTime())));

const rowDefaults = (delay: DelayView | null): DelayFormData =>
  delay
    ? ({
        holdNo: String(delay.holdNo),
        delayTypeCode: delay.delayTypeCode,
        delayCode: delay.delayCode,
        startAt: uaeWallClock(delay.startAt) ?? undefined,
        endAt: uaeWallClock(delay.endAt) ?? undefined,
        loaderCode: delay.loaderCode ?? "",
        remarks: delay.remarks ?? "",
        delayTime: formatMinutes(delay.durationMinutes),
      } as unknown as DelayFormData)
    : delayDefaultValues;

/**
 * DBCO-0059 (R-30, AD-96) - the Add / Update delay side drawer. Hold No and Loader are selects of the operation's own sequence
 * holds and loaders; the Delay code list follows the chosen type; a retired type or code on Update stays selectable. The window
 * rules (start < end, start not before the first ATS, end not after now / the last ATC) are mirrored from the server, which stays
 * authoritative. The draft is kept per operation and per delay (L4) and cleared on Cancel and on success.
 */
const DelayDrawer: React.FC<{
  operationId: string;
  delay: DelayView | null;
  onClose: () => void;
}> = ({ operationId, delay, onClose }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  useGatedOpen(true);
  const detail = useCachedCargoOperationDetail(operationId).data;
  const sequences = useMemo(() => detail?.sequences ?? [], [detail?.sequences]);
  const { add, update } = useDelayMutations(operationId);
  const delayId = delay?.id ?? null;

  const storedTarget = useDelayFormStore((state) => state.target);
  const storedValues = useDelayFormStore((state) => state.values);
  const setDraft = useDelayFormStore((state) => state.setDraft);
  const resetStore = useDelayFormStore((state) => state.reset);
  const matches =
    storedTarget !== null &&
    storedTarget.operationId === operationId &&
    storedTarget.delayId === delayId;
  const storeValues = useMemo(
    () => (matches ? (storedValues as Partial<DelayFormData>) : {}),
    [matches, storedValues],
  );

  const status = detail?.status;
  const window = useMemo<DelayWindow>(
    () => ({
      status,
      firstAts: earliest(
        sequences.flatMap((sequence) => {
          const value = uaeWallClock(sequence.ats);
          return value ? [value] : [];
        }),
      ),
      lastAtc: latest(
        sequences.flatMap((sequence) => {
          const value = uaeWallClock(sequence.atc);
          return value ? [value] : [];
        }),
      ),
      now: getCurrentUaeDate,
    }),
    [status, sequences],
  );
  const schema = useMemo(() => delaySchema(t, window), [t, window]);
  const defaults = useMemo(() => rowDefaults(delay), [delay]);

  const form = usePersistentForm<DelayFormData>({
    schema,
    defaultValues: defaults,
    storeValues,
    setStoreValues: (values) =>
      setDraft({ operationId, delayId }, values as Record<string, unknown>),
    resetStore,
    persistKey: DELAY_FORM_PERSIST_KEY,
    dateKeys: [FIELDS.START_AT, FIELDS.END_AT],
  });
  const { watch, setValue } = form;

  useEffect(() => {
    if (storedTarget !== null && !matches) {
      resetStore();
    }
  }, [storedTarget, matches, resetStore]);

  const discarded = useRef(false);
  useEffect(() => {
    const subscription = watch((values) => {
      if (!discarded.current) {
        setDraft({ operationId, delayId }, values as Record<string, unknown>);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setDraft, operationId, delayId]);

  const delayTypeCode = watch(FIELDS.DELAY_TYPE_CODE);
  const startAt = watch(FIELDS.START_AT);
  const endAt = watch(FIELDS.END_AT);

  useEffect(() => {
    if (startAt instanceof Date && endAt instanceof Date && endAt.getTime() > startAt.getTime()) {
      setValue(
        FIELDS.DELAY_TIME as never,
        formatMinutes(Math.round((endAt.getTime() - startAt.getTime()) / 60_000)) as never,
      );
    } else {
      setValue(FIELDS.DELAY_TIME as never, "" as never);
    }
  }, [startAt, endAt, setValue]);

  const types = useDelayTypes();
  const codes = useDelayCodes(delayTypeCode);

  const holdOptions = useMemo<Option[]>(
    () =>
      withSaved(
        [...new Set(sequences.map((sequence) => sequence.hold))]
          .sort((left, right) => left - right)
          .map((hold) => ({ label: String(hold), value: String(hold) })),
        delay ? { label: String(delay.holdNo), value: String(delay.holdNo) } : null,
      ),
    [sequences, delay],
  );
  const loaderOptions = useMemo<Option[]>(
    () =>
      withSaved(
        [
          ...new Set(
            sequences.flatMap((sequence) => (sequence.loaderCode ? [sequence.loaderCode] : [])),
          ),
        ]
          .sort()
          .map((loader) => ({ label: loader, value: loader })),
        delay?.loaderCode ? { label: delay.loaderCode, value: delay.loaderCode } : null,
      ),
    [sequences, delay],
  );
  const typeOptions = useMemo<Option[]>(
    () =>
      withSaved(
        (types.data ?? []).map((type) => ({
          label: type.delayTypeCode,
          value: type.delayTypeCode,
        })),
        delay ? { label: delay.delayTypeCode, value: delay.delayTypeCode } : null,
      ),
    [types.data, delay],
  );
  const codeOptions = useMemo<Option[]>(
    () =>
      withSaved(
        (codes.data ?? []).map((code) => ({
          label: `${code.delayCode} - ${code.delayDescription ?? ""}`,
          value: code.delayCode,
        })),
        delay && delayTypeCode === delay.delayTypeCode
          ? {
              label: `${delay.delayCode} - ${delay.delayDescription ?? ""}`,
              value: delay.delayCode,
            }
          : null,
      ),
    [codes.data, delay, delayTypeCode],
  );

  const onValid = async (values: DelayFormData) => {
    const request: DelayRequest = {
      holdNo: Number(values.holdNo),
      delayTypeCode: values.delayTypeCode,
      delayCode: values.delayCode,
      startAt: convertUaeToUtc(values.startAt),
      endAt: convertUaeToUtc(values.endAt),
      loaderCode: values.loaderCode ? values.loaderCode : null,
      remarks: values.remarks?.trim() ? values.remarks.trim() : null,
    };
    try {
      if (delay) {
        await update.mutateAsync({ delayId: delay.id, request });
      } else {
        await add.mutateAsync(request);
      }
      notify({ type: "success", title: t(`${L_DELAY}.${delay ? "updated" : "added"}`) });
      discardDraft();
      onClose();
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t(`${L_DELAY}.save-failed`),
      });
    }
  };

  const onInvalid = () =>
    notify({
      type: "error",
      title: t(`${L_DELAY}.validation-error-title`),
      description: t(`${L_DELAY}.validation-error-description`),
    });

  const discardDraft = () => {
    discarded.current = true;
    form.clear();
    resetStore();
  };

  const cancel = () => {
    discardDraft();
    onClose();
  };

  return (
    <SideDrawer
      isOpen
      title={t(`${L_DELAY}.${delay ? "title-update" : "title-add"}`)}
      onClose={onClose}
      onCancel={cancel}
      onSubmit={form.handleSubmit(onValid, onInvalid)}
      isSubmitting={add.isPending || update.isPending}
      submitLabel={t(`${L_DELAY}.submit`)}
      cancelLabel={t(`${L_DELAY}.cancel`)}
      width={560}
    >
      <div data-testid="delay-drawer" className="w-full flex flex-col gap-2 px-6 py-2">
        <FormProvider {...form}>
          <FormItem<DelayFormData, "holdNo">
            required
            name={FIELDS.HOLD_NO}
            type="select"
            label={t(`${L_DELAY}.fields.hold-no`)}
            testId="delay-hold-no"
            options={holdOptions}
          />
          <FormItem<DelayFormData, "delayTypeCode">
            required
            name={FIELDS.DELAY_TYPE_CODE}
            type="select"
            label={t(`${L_DELAY}.fields.delay-type`)}
            testId="delay-type"
            options={typeOptions}
            onFieldChange={() => setValue(FIELDS.DELAY_CODE, "" as never)}
          />
          <FormItem<DelayFormData, "delayCode">
            required
            name={FIELDS.DELAY_CODE}
            type="select"
            label={t(`${L_DELAY}.fields.delay-code`)}
            testId="delay-code"
            options={codeOptions}
            disabled={!delayTypeCode}
          />
          <FormItem<DelayFormData, "startAt">
            required
            name={FIELDS.START_AT}
            type="date-picker"
            timePicker
            combinedInput
            dateConstraint="not-future"
            label={t(`${L_DELAY}.fields.start`)}
            testId="delay-start"
          />
          <FormItem<DelayFormData, "endAt">
            required
            name={FIELDS.END_AT}
            type="date-picker"
            timePicker
            combinedInput
            dateConstraint="not-future"
            label={t(`${L_DELAY}.fields.end`)}
            testId="delay-end"
          />
          <FormItem<DelayFormData, "delayTime">
            disabled
            name={FIELDS.DELAY_TIME}
            type="text"
            label={t(`${L_DELAY}.fields.delay-time`)}
            testId="delay-time"
          />
          <FormItem<DelayFormData, "loaderCode">
            name={FIELDS.LOADER_CODE}
            type="select"
            label={t(`${L_DELAY}.fields.loader`)}
            testId="delay-loader"
            options={[{ label: "-", value: "" }, ...loaderOptions]}
          />
          <FormItem<DelayFormData, "remarks">
            name={FIELDS.REMARKS}
            type="textarea"
            label={t(`${L_DELAY}.fields.remarks`)}
            testId="delay-remarks"
            maxRows={4}
            maxChars={REMARKS_MAX_LENGTH}
          />
        </FormProvider>
      </div>
    </SideDrawer>
  );
};

export default DelayDrawer;
